import { Box, Chip, Paper, Skeleton, Typography } from '@mui/material';
import { useEffect } from 'react';

import { useDepletionsKpis } from '../api/depletions';
import { usePageHeading } from '../pages/PageHeadingContext';
import { colors } from '../theme';
import { coverageRangeLabel } from '../util/dates';

const wholeFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

interface GuideItem {
  icon: string;
  label: string;
  desc: string;
}

interface GuideGroup {
  category: string;
  categoryColor: string;
  step: number;
  items: GuideItem[];
}

const GROUPS: GuideGroup[] = [
  {
    category: 'Overview & Action',
    categoryColor: '#4f46e5',
    step: 1,
    items: [
      { icon: '📊', label: 'Health Snapshot', desc: 'KPIs at a glance' },
      { icon: '🧾', label: '2026 Orders', desc: "Each account's orders, by month" },
      { icon: '🏪', label: 'Channel Mix', desc: 'On / off-premise + online retailers' },
      { icon: '🔔', label: 'Follow-Up Actions', desc: 'Who to call and when' },
    ],
  },
  {
    category: 'Trends & Performance',
    categoryColor: '#3b82f6',
    step: 2,
    items: [
      { icon: '📈', label: 'Volume Trends', desc: 'Monthly patterns over time' },
      { icon: '🥃', label: 'Product Performance', desc: 'Top SKUs and momentum' },
      { icon: '🗺️', label: 'State Breakdown', desc: 'Where volume concentrates' },
    ],
  },
  {
    category: 'Account Intelligence',
    categoryColor: '#059669',
    step: 3,
    items: [
      { icon: '🏆', label: 'Top Accounts', desc: 'Your highest-volume buyers' },
      { icon: '🗓️', label: 'Activity Heatmap', desc: 'Reorder cadence & gaps per account' },
    ],
  },
  {
    category: 'Base Health',
    categoryColor: '#f59e0b',
    step: 4,
    items: [
      { icon: '🆕', label: 'New vs Lost', desc: 'Is the account base growing?' },
      { icon: '📉', label: 'Growth & Decline', desc: 'Who is trending up or down' },
    ],
  },
  {
    category: 'Plan Ahead',
    categoryColor: '#8b5cf6',
    step: 5,
    items: [{ icon: '🎯', label: 'What-If Simulator', desc: 'Stress-test next quarter' }],
  },
];

export function DepletionsDashboardGuide() {
  const { data } = useDepletionsKpis();
  const { setActions } = usePageHeading();

  const rangeLabel =
    data?.period_start && data.period_end
      ? coverageRangeLabel(data.period_start, data.period_end, data.data_through)
      : null;
  const statesLabel = data ? wholeFormatter.format(data.unique_states) : null;
  const accountsLabel = data ? wholeFormatter.format(data.unique_accounts) : null;

  // Push dataset context (date range + scope) into the page heading's
  // right-side slot.  Renders as a tight, right-aligned info group on
  // the same row as the H1 — Stripe / Linear / Datadog pattern.  Clear
  // the slot on unmount so navigating away doesn't leave stale data.
  useEffect(() => {
    setActions(
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
        {rangeLabel ? (
          <Chip
            label={rangeLabel}
            size="small"
            sx={{
              bgcolor: `${colors.chartPrimary}12`,
              color: colors.chartPrimary,
              fontWeight: 600,
              fontSize: 11.5,
              height: 24,
              borderRadius: 1.5,
              letterSpacing: '0.01em',
            }}
          />
        ) : (
          // Sized for the full label incl. the "(through Aug 23)" suffix,
          // so the pill doesn't visibly grow when the data lands.
          <Skeleton width={210} height={24} sx={{ borderRadius: 1.5 }} />
        )}
        {statesLabel && accountsLabel ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: 12,
              color: colors.textMuted,
            }}
          >
            <Box
              sx={{
                width: 3,
                height: 3,
                borderRadius: '50%',
                bgcolor: colors.textMuted,
                opacity: 0.55,
                display: { xs: 'none', sm: 'block' },
              }}
            />
            <Typography sx={{ fontSize: 12, color: colors.textMuted, whiteSpace: 'nowrap' }}>
              <Box component="span" sx={{ fontWeight: 600, color: colors.textPrimary }}>
                {statesLabel}
              </Box>{' '}
              states
              <Box component="span" sx={{ mx: 0.6, color: colors.textMuted, opacity: 0.6 }}>
                ·
              </Box>
              <Box component="span" sx={{ fontWeight: 600, color: colors.textPrimary }}>
                {accountsLabel}
              </Box>{' '}
              accounts
            </Typography>
          </Box>
        ) : (
          <Skeleton width={180} height={16} />
        )}
      </Box>,
    );
    return () => setActions(null);
  }, [setActions, rangeLabel, statesLabel, accountsLabel]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Dashboard guide card */}
      <Paper
        elevation={0}
        sx={{
          px: 2.5,
          py: 2,
          borderRadius: 2,
          bgcolor: '#fff',
          border: `1px solid ${colors.border}`,
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: 15, md: 17 },
            fontWeight: 600,
            color: colors.textPrimary,
            mb: 0.5,
            letterSpacing: '-0.01em',
            lineHeight: 1.3,
          }}
        >
          What this dashboard helps you with
        </Typography>
        <Typography sx={{ fontSize: 12, color: colors.textMuted, mb: 2 }}>
          Scroll through each section to go from a high-level overview to specific account-level
          actions.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            // With 5 groups, stacking until lg keeps each card readable
            // on tablets (md ~900px content area). Below lg, each group
            // becomes its own full-width card with a top accent rule.
            flexDirection: { xs: 'column', lg: 'row' },
            gap: { xs: 1, lg: 0 },
            alignItems: 'stretch',
          }}
        >
          {GROUPS.map((group, gi) => {
            const next = GROUPS[gi + 1];
            return (
              <Box key={group.category} sx={{ display: 'flex', alignItems: 'stretch', flex: 1 }}>
                {/* Category card */}
                <Box
                  sx={{
                    flex: 1,
                    px: 2,
                    py: 1.5,
                    borderLeft: { lg: `3px solid ${group.categoryColor}` },
                    borderTop: { xs: `3px solid ${group.categoryColor}`, lg: 'none' },
                    bgcolor: `${group.categoryColor}05`,
                    borderRadius: { xs: 1.5, lg: 0 },
                  }}
                >
                  {/* Step number + category */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        flexShrink: 0,
                        bgcolor: group.categoryColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#fff' }}>
                        {group.step}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: group.categoryColor,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                      }}
                    >
                      {group.category}
                    </Typography>
                  </Box>

                  {/* Items */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.85 }}>
                    {group.items.map((item) => (
                      <Box
                        key={item.label}
                        sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.85 }}
                      >
                        <Typography sx={{ fontSize: 14, lineHeight: 1.3, flexShrink: 0 }}>
                          {item.icon}
                        </Typography>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: colors.textPrimary,
                              lineHeight: 1.25,
                            }}
                          >
                            {item.label}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 10.5,
                              color: colors.textMuted,
                              lineHeight: 1.35,
                            }}
                          >
                            {item.desc}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Connector arrow — only shown when groups sit in a row. */}
                {next && (
                  <Box
                    sx={{
                      display: { xs: 'none', lg: 'flex' },
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 0.75,
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${group.categoryColor}26, ${next.categoryColor}26)`,
                        border: `2px solid ${next.categoryColor}40`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 18,
                          fontWeight: 700,
                          color: next.categoryColor,
                          lineHeight: 1,
                        }}
                      >
                        →
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
}
